<template>
  <v-slide-x-reverse-transition>
    <v-card class="elevation-5 condition-details vop-bg-whisper" v-show="isVisible">
      <div>
        <!-- Header title -->
        <v-toolbar flat color="vop-bg-whisper">
          <v-toolbar-title class="subheading ttu font-weight-medium">
            <span>{{ messages.label.ADD_RULES }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="hide()">
            <v-icon medium>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <!-- Rules search filter -->
          <v-layout row wrap>
            <!-- Rule State -->
            <v-flex md2 sm2>
              <v-select v-model="selectedRuleState" :label="messages.label.RULE_STATE" :items="ruleStateList"
                item-text="text" item-value="value" return-object></v-select>
            </v-flex>
            <!-- Rule Title -->
            <v-flex md3 sm3>
              <v-text-field :label="messages.label.RULE_TITLE" v-model="ruleTitle" @keyup.enter="searchRules()"
                clearable></v-text-field>
            </v-flex>
            <!-- Search Button -->
            <v-flex md2 sm2>
              <v-btn color="primary" @click="searchRules()">{{ messages.label.SEARCH }}</v-btn>
            </v-flex>
          </v-layout>
          <!-- Rules serach result -->
          <v-layout row wrap>
            <v-flex>
              <v-data-table :headers="ruleHeaders" v-model="selectedRules" :items="rulesList"
                :pagination.sync="pagination" :total-items="totalItems" :loading="rulesLoading"
                :rows-per-page-items="rowsPerPagesArray">
                <!-- Items -->
                <template slot="items" slot-scope="props">
                  <tr>
                    <td>
                      <v-checkbox v-model="props.selected" primary hide-details></v-checkbox>
                    </td>
                    <td class="text-xs-left">{{ props.item.title }}</td>
                    <td class="text-xs-left">{{ getProviderName(props.item.secondaryOwner) }}</td>
                    <td class="text-xs-left">{{ getRuleConditionsCount(props.item.conditions) }}</td>
                    <td class="text-xs-left">{{ props.item._enabled }}</td>
                    <td @click.stop="stopProp" class="justify-center layout px-0">
                      <!-- View Details icon -->
                      <v-tooltip bottom slot="activator">
                        <template v-slot:activator="{ on }">
                          <v-icon small v-on="on" @click="showRuleQuickView(props.item)">
                            open_in_new
                          </v-icon>
                        </template>
                        <span>View Details</span>
                      </v-tooltip>
                      <!-- Mode Menu -->
                      <v-menu bottom left :disabled="rulesLoading">
                        <v-tooltip bottom slot="activator">
                          <template v-slot:activator="{ on }">
                            <v-btn icon medium v-on="on" class="grey--text text--darken-2">
                              <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                          </template>
                          <span>More</span>
                        </v-tooltip>
                        <v-list>
                          <v-list-tile>
                            <v-list-tile-title>{{ messages.label.TEST }}</v-list-tile-title>
                          </v-list-tile>
                          <v-list-tile @click="goToEditView(props.item)">
                            <v-list-tile-title>{{ messages.label.EDIT }}</v-list-tile-title>
                            <v-icon small right>exit_to_app</v-icon>
                          </v-list-tile>
                        </v-list>
                      </v-menu>
                    </td>
                  </tr>
                </template>
                <!-- No Data -->
                <template slot="no-data">
                  <v-alert :value="true" color="info" class="black--text" icon="info">
                    <span v-if="!initiatedSearch">
                      {{ messages.info.SELECT_SEARCH_CRITERIA }}
                    </span>
                    <span v-else>
                      {{ messages.info.NO_SEARCH_RESULTS }}
                    </span>
                  </v-alert>
                </template>
              </v-data-table>
            </v-flex>
          </v-layout>
          <!-- Snackbar -->
          <v-flex xs12>
            <v-snackbar :bottom="true" :timeout="snackbar.timeout" :color="snackbar.color" v-model="snackbar.show">
              {{ snackbar.message }}
              <v-btn flat color="secondary" @click.native="snackbar.show = false">
                {{ messages.label.OK }}
              </v-btn>
            </v-snackbar>
          </v-flex>
        </v-card-text>
      </div>
      <!-- Dialogs -->
      <v-dialog v-model="ruleQuickView" width="1000" height="800">
        <RuleQuickView />
      </v-dialog>
      <!-- Actions -->
      <v-card-actions class="px-3">
        <v-spacer />
        <v-btn color="primary" class="ml-2" :disabled="selectedRules.length === 0" @click="updateRulesInRuleSet">
          {{ messages.label.ADD_SELECTED }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-slide-x-reverse-transition>
</template>

<script>
import { mapState, mapGetters, mapMutations } from "vuex"
import messages from "@/MetadataRules/messages.json"
import MetadataRulesService from "@/MetadataRules/MetadataRulesService"
import * as metadataRulesDataService from "@/MetadataRules/MetadataRulesDataService"
import RuleQuickView from "@/MetadataRules/Rule/RuleQuickView"
import { RULE_SET_HAS_RULES, MVATS_WEB_SERVICE_URL, RULE_MGMT_DATA_SERVICE_URL } from "@/store/constants/getter-types"
import { SET_RULESET_RULES } from "@/store/constants/mutation-types"

export default {
  name: "AddRules",
  components: {
    RuleQuickView
  },
  computed: {
    ...mapState([
      "mpxParentAccount"
    ]),
    ...mapState("metadataRules", {
      ruleSetRulesList: state => state.ruleSetRulesList,
      addRulesFilters: state => state.addRulesFilters
    }),
    ...mapGetters("metadataRules", [
      RULE_SET_HAS_RULES
    ]),
    ...mapGetters([
      MVATS_WEB_SERVICE_URL, RULE_MGMT_DATA_SERVICE_URL
    ])
  },
  watch: {
    /**
     * Watch handler for pagination object
     */
    pagination: {
      handler(newVal, oldVal) {
        if (this.initiatedSearch && oldVal !== newVal) {
          this.searchRules()
        }
      },
      deep: true
    }
  },
  data() {
    return {
      isVisible: true,
      ruleStateList: this.getRuleStates(),
      initiatedSearch: false,
      selectedRuleState: { text: "All", value: "all" },
      ruleTitle: "",
      ruleHeaders: this.getRuleHeaders(),
      rulesList: [],
      selectedRules: [],
      pagination: {
        sortBy: "title",
        page: 1,
        rowsPerPage: 10,
        descending: false
      },
      snackbar: {
        show: false,
        timeout: 5000,
        message: "",
        color: "error"
      },
      totalItems: 0,
      rulesLoading: false,
      rowsPerPagesArray: [10, 25],
      ruleQuickView: false
    }
  },
  methods: {
    ...mapMutations("metadataRules", [
      SET_RULESET_RULES
    ]),
    /**
     * getRuleHeaders - get headers array
     * @returns {array}
     */
    getRuleHeaders() {
      return [
        {
          text: "",
          sortable: false
        },
        {
          text: messages.label.TH_TITLE,
          align: "left",
          sortable: false,
          value: "title"
        },
        {
          text: messages.label.TH_PROVIDER,
          sortable: false,
          align: "left",
          value: "secondaryOwner"
        },
        {
          text: messages.label.TH_CONDITIONS_COUNT,
          sortable: false,
          align: "left",
          value: "conditions"
        },
        {
          text: messages.label.TH_ENABLED,
          sortable: false,
          align: "left",
          value: "_enabled"
        },
        {
          text: "",
          sortable: false
        }
      ]
    },

    /**
     * stopProp - Do nothing, just to stop event propogation
     */
    stopProp() { },

    /**
     * Hide side sheet view
     */
    hide() {
      this.isVisible = false
      this.resetData()
      MetadataRulesService.$emit("event:hideAddRulesSheet")
    },

    /**
     * Show add rules sheet view
     */
    show() {
      this.isVisible = true
    },

    /**
     * resetData
     */
    resetData() {
      this.selectedRules = []
      this.rulesList = []
      this.initiatedSearch = false
      this.selectedRuleState = { text: "All", value: "all" }
      this.ruleTitle = ""
      this.totalItems = 0
    },

    /**
     * Get pagination params
     */
    getPageParams(pagination) {
      let startRange
      let endRange
      if (pagination.page === 1) {
        startRange = 1
        endRange = pagination.rowsPerPage
      } else {
        startRange = (pagination.page - 1) * pagination.rowsPerPage + 1
        endRange = startRange + pagination.rowsPerPage - 1
      }
      return startRange + "-" + endRange
    },

    /**
     * getSortParams - Construct sorting param
     *
     * @param  {object} pagination pagination object
     * @return {string}            ex: '<filedName>|desc'
     */
    getSortParams(pagination) {
      let sortParams = ""
      sortParams =
        !this.$lodash.isNull(pagination.sortBy) &&
          !this.$lodash.isEmpty(pagination.sortBy)
          ? pagination.sortBy
          : "title"
      if (pagination.descending) {
        sortParams += "|desc"
      }
      return sortParams
    },

    /**
     * getRuleStates
     * @returns {array}
     */
    getRuleStates() {
      return [
        { text: "All", value: "all" },
        { text: "Enabled", value: "enabled" },
        { text: "Disabled", value: "disabled" }
      ]
    },

    /**
     * getRuleConditionsCount - return count of conditions exists in a rule
     * @param {array} conditionsArray
     * @returns {number}
     */
    getRuleConditionsCount(conditionsArray) {
      if (!this.$lodash.isUndefined(conditionsArray)) {
        return conditionsArray.length
      } else {
        return 0
      }
    },

    /**
     * searchRules - trigger the search event for retrieving all the rules, rule sets and rule collection
     */
    searchRules() {
      this.rulesLoading = true
      let queryObject = {}

      // Disabled query
      if (this.selectedRuleState.value === "enabled") {
        queryObject.byDisabled = false
      } else if (this.selectedRuleState.value === "disabled") {
        queryObject.byDisabled = true
      }
      // Title Query
      if (this.ruleTitle !== "") {
        queryObject.byTitlePrefix = this.ruleTitle
      }
      // TYPE
      if (!this.$lodash.isUndefined(this.addRulesFilters.ruleSetType)) {
        queryObject.byType = this.addRulesFilters.ruleSetType
      }
      // Account context
      if (!this.$lodash.isUndefined(this.addRulesFilters.ruleSetFor)) {
        if (this.addRulesFilters.ruleSetFor === "account") {
          // Pass provider group account context
          queryObject.account = this.addRulesFilters.providerGroupAccountId
        } else {
          // Pass parent account context
          queryObject.account = this.mpxParentAccount.id
        }
      }

      // Omit 'grouping' as it's client only
      queryObject.range = this.getPageParams(this.pagination)
      queryObject.sort = this.getSortParams(this.pagination)
      queryObject.count = true

      return metadataRulesDataService
        .getRule(queryObject,
          this.RULE_MGMT_DATA_SERVICE_URL)
        .then((response) => {
          this.rulesList = MetadataRulesService.addClientOnlyFields(response.data.entries)
          this.totalItems = response.data.totalResults
          return response
        }).catch((error) => {
          // TODO: handle error
          this.rulesList = []
          this.totalItems = 0
          return Promise.reject(error)
        }).finally(() => {
          this.rulesLoading = false
          this.initiatedSearch = true
        })
    },

    /**
     * updateRuleSelection - update rule selection for ruleset
     */
    updateRulesInRuleSet() {
      if (this.RULE_SET_HAS_RULES) {
        // Already have some rules, so update
        let ruleSetRulesToUpdate = this.$lodash.cloneDeep(this.ruleSetRulesList)
        this.$lodash.forEach(this.selectedRules, (value) => {
          let isInListIndex = this.$lodash.findIndex(ruleSetRulesToUpdate, ["id", value.id])
          if (isInListIndex === -1) {
            ruleSetRulesToUpdate.push(value)
          } else {
            ruleSetRulesToUpdate.splice(isInListIndex, 1, value)
          }
        })
        this.SET_RULESET_RULES(ruleSetRulesToUpdate)
      } else {
        this.SET_RULESET_RULES(this.selectedRules)
      }
    },

    /**
     * goToEditView - Navigate to Edit view
     */
    goToEditView(rule) {
      let id = rule.id.split("/").pop()
      let ownerId = rule.ownerId
      let location = "/#/metadata-rules/rule/" + id + "?account=" + ownerId
      window.open(location, "_blank")
    },

    /**
     * showRuleQuickView - Show rule quick view in dialog
     */
    showRuleQuickView(rule) {
      this.ruleQuickView = true
      MetadataRulesService.$emit("event:showRuleQuickViewDialog", rule)
    },

    /**
     * Hide quick view dialog
     */
    hideRuleQuickView() {
      this.ruleQuickView = false
    },

    /**
     * updateSearchResults - Update serach results if some results exist
     */
    updateSearchResults() {
      if (this.rulesList.length > 0) {
        if (this.pagination.page > 1) {
          this.pagination.page = 1 // Triggers search
        }
        this.searchRules()
      }
    },

    /**
     * getOwnerName
     * @param {stirng} secondaryOwner
     */
    getProviderName(secondaryOwner) {
      return MetadataRulesService.getProviderName(secondaryOwner)
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Initial data
     */
    /**
     * Events
     */
    MetadataRulesService.$on("event:showAddRulesSheet", this.show)
    MetadataRulesService.$on("event:ruleDataRetrieved", this.hide)
    MetadataRulesService.$on("event:hideRuleQuickViewDialog", this.hideRuleQuickView)
    MetadataRulesService.$on("event:addRulesFilterUpdated", this.updateSearchResults)
  },
  beforeDestroy() {
    MetadataRulesService.$off("event:showAddRulesSheet", this.show)
    MetadataRulesService.$off("event:ruleDataRetrieved", this.hide)
    MetadataRulesService.$off("event:hideRuleQuickViewDialog", this.hideRuleQuickView)
    MetadataRulesService.$off("event:addRulesFilterUpdated", this.updateSearchResults)
  }
}
</script>

<style lang="scss">
.condition-details {
  min-height: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
</style>



// WEBPACK FOOTER //
// src/MetadataRules/RuleSet/AddRules.vue